import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  Menu,
  Portal,
  Spinner,
  Stack,
  Table,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { LuChevronDown, LuDownload, LuEye, LuFileSpreadsheet, LuFileText, LuPlus, LuSearch } from 'react-icons/lu';
import { listContracts, setTitlingStage, type ContractListRow, type TitlingStage } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useMe } from '@/lib/MeContext';
import { toaster } from '@/components/ui/toaster';
import { TitlingStageBadge, TitlingStageSelect } from '@/components/contract/titling-stage';
import { fmtMoney, fmtPct } from '@/utils/format';
import { exportCartera, exportEstados, EmptyExportError } from '@/utils/reports';

const METHOD_LABEL: Record<ContractListRow['method'], string> = {
  french: 'Francés',
  german: 'Alemán',
};

type StatusTab = 'active' | 'completed' | 'cancelled';

export function Contracts() {
  const navigate = useNavigate();
  const { activeProject } = useProject();
  const { me } = useMe();
  const isAdmin = me?.role === 'admin';
  const [tab, setTab] = useState<StatusTab>('active');
  const [params, setParams] = useSearchParams();
  const hasOverdue = params.get('hasOverdue') === '1';
  const buyerId = params.get('buyerId') || undefined;

  function toggleHasOverdue(next: boolean) {
    const p = new URLSearchParams(params);
    if (next) p.set('hasOverdue', '1');
    else p.delete('hasOverdue');
    setParams(p, { replace: true });
  }
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [rows, setRows] = useState<ContractListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  async function runExport(fn: () => Promise<void>) {
    if (!activeProject || exporting) return;
    setExporting(true);
    try {
      await fn();
    } catch (err) {
      if (err instanceof EmptyExportError) {
        toaster.create({ title: 'No hay contratos para exportar', type: 'info' });
      } else {
        toaster.create({
          title: 'No se pudo exportar',
          description: err instanceof Error ? err.message : '',
          type: 'error',
        });
      }
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!activeProject) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listContracts({
      projectId: activeProject.id,
      status: tab,
      q: debouncedQ || undefined,
      buyerId,
      hasOverdue: hasOverdue || undefined,
    })
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'No se pudo cargar la lista');
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProject?.id, tab, debouncedQ, hasOverdue, buyerId]);

  async function handleStageChange(id: string, next: TitlingStage | null) {
    const prev = rows;
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, titlingStage: next } : r)));
    try {
      await setTitlingStage(id, next);
    } catch (err) {
      setRows(prev);
      toaster.create({
        title: 'No se pudo actualizar la etapa',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    }
  }

  if (!activeProject) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="10" textAlign="center" maxW="2xl" mx="auto">
          <Text color="fg.muted">Selecciona un proyecto en la barra superior para ver sus contratos.</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="6xl">
        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'flex-end' }} gap="4">
          <Stack gap="1">
            <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
              {activeProject.code} · {activeProject.name}
            </Text>
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              Contratos
            </Heading>
            <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
              Contratos financiados y de pago al contado.
            </Text>
          </Stack>
          <HStack gap="2" alignSelf={{ base: 'stretch', md: 'flex-end' }} wrap="wrap">
            <Button
              variant="outline"
              disabled={exporting}
              onClick={() => runExport(() => exportCartera(activeProject.id, activeProject.code, tab))}
            >
              <Icon as={LuDownload} /> Exportar cartera
            </Button>
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button variant="outline" disabled={exporting}>
                  <Icon as={LuFileText} /> Estados de cuenta <Icon as={LuChevronDown} />
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item
                      value="excel"
                      onClick={() => runExport(() => exportEstados(activeProject.id, activeProject.code, tab, 'excel'))}
                    >
                      <Icon as={LuFileSpreadsheet} /> Excel
                    </Menu.Item>
                    <Menu.Item
                      value="pdf"
                      onClick={() => runExport(() => exportEstados(activeProject.id, activeProject.code, tab, 'pdf'))}
                    >
                      <Icon as={LuFileText} /> PDF
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
            <Button
              colorPalette="brand"
              bg="brand.600"
              color="white"
              _hover={{ bg: 'brand.700' }}
              onClick={() => navigate('/contratos/nuevo')}
            >
              <Icon as={LuPlus} /> Nuevo contrato
            </Button>
          </HStack>
        </Stack>

        <Tabs.Root value={tab} onValueChange={(v) => setTab(v.value as StatusTab)}>
          <Stack direction={{ base: 'column', md: 'row' }} gap="3" align={{ base: 'stretch', md: 'center' }} justify="space-between">
            <Tabs.List>
              <Tabs.Trigger value="active">Activos</Tabs.Trigger>
              <Tabs.Trigger value="completed">Pagados</Tabs.Trigger>
              <Tabs.Trigger value="cancelled">Cancelados</Tabs.Trigger>
            </Tabs.List>
            <InputGroup startElement={<Icon as={LuSearch} color="fg.muted" />} w={{ base: 'full', md: '320px' }}>
              <Input value={q} onChange={(e) => setQ(e.currentTarget.value)} placeholder="Buscar por número o cliente…" />
            </InputGroup>
            <Checkbox.Root
              checked={hasOverdue}
              onCheckedChange={(d) => toggleHasOverdue(Boolean(d.checked))}
              size="sm"
              colorPalette="brand"
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>Sólo con vencidas</Checkbox.Label>
            </Checkbox.Root>
          </Stack>

          <Box pt="6">
            {error && (
              <Text color="red.600" fontSize="sm" mb="4">
                {error}
              </Text>
            )}
            {loading ? (
              <HStack gap="3" color="fg.muted">
                <Spinner size="sm" />
                <Text fontSize="sm">Cargando…</Text>
              </HStack>
            ) : rows.length === 0 ? (
              <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="10" textAlign="center">
                <Text color="fg.muted" fontSize="sm">
                  {debouncedQ
                    ? `No hay contratos que coincidan con "${debouncedQ}".`
                    : tab === 'active'
                      ? 'Aún no hay contratos en este proyecto. Crea el primero para empezar.'
                      : tab === 'completed'
                        ? 'No hay contratos pagados en este proyecto.'
                        : 'No hay contratos cancelados en este proyecto.'}
                </Text>
              </Box>
            ) : (
              <>
                {/* Mobile: card list */}
                <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
                  {rows.map((c) => (
                    <Box key={c.id} bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="4">
                      <Stack gap="3">
                        <HStack justify="space-between" align="flex-start" gap="3">
                          <Stack gap="0.5" minW={0}>
                            <Text fontSize="xs" color="fg.muted" fontWeight="700">
                              {c.contractNumber}
                            </Text>
                            <Text fontWeight="600" lineHeight="1.2">
                              {c.buyerName}
                            </Text>
                            <Text fontSize="sm" color="fg.muted">
                              {c.buyerCode ?? '—'} · {c.saleType === 'cash' ? 'Contado' : METHOD_LABEL[c.method]}
                            </Text>
                          </Stack>
                          <Badge colorPalette={c.status === 'active' ? 'green' : c.status === 'completed' ? 'blue' : 'gray'} flexShrink={0}>
                            {c.status === 'active' ? 'Activo' : c.status === 'completed' ? 'Pagado' : 'Cancelado'}
                          </Badge>
                        </HStack>
                        <HStack justify="space-between" fontSize="sm" color="fg.muted">
                          <Text>{fmtMoney(c.financedAmount)}</Text>
                          <Text>{c.saleType === 'cash' ? 'Contado' : `${c.termMonths} meses · ${fmtPct(c.interestRateAnnual)}`}</Text>
                        </HStack>
                        <HStack justify="space-between" gap="3">
                          <Text fontSize="sm" color="fg.muted">
                            Escrituración
                          </Text>
                          {isAdmin ? (
                            <TitlingStageSelect value={c.titlingStage} onChange={(next) => handleStageChange(c.id, next)} />
                          ) : (
                            <TitlingStageBadge stage={c.titlingStage} />
                          )}
                        </HStack>
                        <Button size="xs" variant="outline" asChild>
                          <RouterLink to={`/contratos/${c.id}`}>
                            <Icon as={LuEye} /> Ver
                          </RouterLink>
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                {/* Desktop: table */}
                <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" overflow="hidden" display={{ base: 'none', md: 'block' }}>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Número</Table.ColumnHeader>
                        <Table.ColumnHeader>Cliente</Table.ColumnHeader>
                        <Table.ColumnHeader>Método</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="end">Financiado</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="end">Plazo / Tasa</Table.ColumnHeader>
                        <Table.ColumnHeader>Estado</Table.ColumnHeader>
                        <Table.ColumnHeader>Escrituración</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="right">Acciones</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rows.map((c) => (
                        <Table.Row key={c.id}>
                          <Table.Cell fontWeight="600">{c.contractNumber}</Table.Cell>
                          <Table.Cell>
                            {c.buyerName}
                            <Text as="span" color="fg.muted">
                              {' '}· {c.buyerCode ?? '—'}
                            </Text>
                          </Table.Cell>
                          <Table.Cell color="fg.muted">{c.saleType === 'cash' ? 'Contado' : METHOD_LABEL[c.method]}</Table.Cell>
                          <Table.Cell textAlign="end">{fmtMoney(c.financedAmount)}</Table.Cell>
                          <Table.Cell textAlign="end" color="fg.muted">
                            {c.saleType === 'cash' ? '—' : `${c.termMonths} m · ${fmtPct(c.interestRateAnnual)}`}
                          </Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette={c.status === 'active' ? 'green' : c.status === 'completed' ? 'blue' : 'gray'}>
                              {c.status === 'active' ? 'Activo' : c.status === 'completed' ? 'Pagado' : 'Cancelado'}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            {isAdmin ? (
                              <TitlingStageSelect value={c.titlingStage} onChange={(next) => handleStageChange(c.id, next)} />
                            ) : (
                              <TitlingStageBadge stage={c.titlingStage} />
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            <HStack justify="flex-end" gap="2">
                              <Button size="xs" variant="outline" asChild>
                                <RouterLink to={`/contratos/${c.id}`}>
                                  <Icon as={LuEye} /> Ver
                                </RouterLink>
                              </Button>
                            </HStack>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>
              </>
            )}
          </Box>
        </Tabs.Root>
      </Stack>
    </Box>
  );
}
